import {
  Component,
  EventEmitter,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  computed,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  AgendaEvent,
  Client,
  Promotion,
  Service,
} from '../../interfaces';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { AgendaService, BusinessService } from '../../../services/business';
import { PromotionsService } from '../../../services/promotions/promotions.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { BaseAuthComponent } from '../../common/BaseAuthComponent.class';
import { EventStatusCode, STATUS_DEF_MAP } from '../../api/eventStatus.token';
import { PeopleAddDrawer } from '../people-add-drawer/people-add-drawer';
import {
  parseDbUtc,
  toDate,
} from '../../common/utils';
import { ToastService } from '../../services';
import { Router } from '@angular/router';
import { Professional } from '../../interfaces';
import { ScheduleService, BusinessScheduleRange } from '../../../services/business/schedule.service';
import { RecurrenceFrequency, RecurrenceSettings } from '../../interfaces';
import {
  AvailabilityCalendarComponent,
  DayAvailability,
  AvailabilityLevel,
} from '../../../features/public-booking/components/availability-calendar/availability-calendar';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

export interface TimeSlot {
  time: string;   // "09:00"
  label: string;  // "9:00 am"
  available: boolean;
  blocked: boolean;
  blockedLabel?: string; // e.g. "Ocupado: Juan - Corte"
}

@Component({
  selector: 'app-event-add',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AsyncPipe,
    PeopleAddDrawer,
    AvailabilityCalendarComponent,
  ],
  templateUrl: './event-add.html',
  styleUrl: './event-add.css',
})
export class EventAdd extends BaseAuthComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Output() created = new EventEmitter<AgendaEvent>();
  @Output() finalPriceChange = new EventEmitter<number>();
  @Input() selectedDate?: string | Date | null;
  @Input() editEventId?: string | null;

  private businessService = inject(BusinessService);
  private agendaApi = inject(AgendaService);
  private scheduleService = inject(ScheduleService);
  private promotionsService = inject(PromotionsService);
  private fb = inject(NonNullableFormBuilder);
  toastService = inject(ToastService);
  private statusDefs = inject(STATUS_DEF_MAP);
  router = inject(Router);

  isSubmitting = false;

  // ── Client ──
  selectedClient = signal<Client | null>(null);

  // ── Multi-service ──
  selectedServices = signal<Service[]>([]);
  isServiceOpen = signal(false);
  private prefetch$ = new Subject<void>();
  private typing$ = new Subject<string>();

  /** Backward-compat: first selected service or null */
  readonly selectedService = computed(() => {
    const s = this.selectedServices();
    return s.length > 0 ? s[0] : null;
  });

  // ── Professional ──
  professionals = signal<Professional[]>([]);
  isLoadingData = signal(false);
  trackById = (_: number, p: Professional) => p.userId;

  // ── Date & Time ──
  selectedDateSignal = signal<Date | null>(null);
  selectedTimeSlot = signal<string | null>(null);
  businessScheduleRanges = signal<BusinessScheduleRange[]>([]);
  dayEvents = signal<AgendaEvent[]>([]);
  isLoadingDayEvents = signal(false);
  monthEvents = signal<AgendaEvent[]>([]);
  isLoadingMonthAvailability = signal(false);
  currentCalendarMonth = signal<{ year: number; month: number }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  /** The selected date formatted as YYYY-MM-DD for the calendar component */
  calendarSelectedDate = computed(() => {
    const d = this.selectedDateSignal();
    if (!d) return null;
    return this.formatDateStr(d);
  });

  /** Per-day availability for the calendar color coding */
  calendarDayAvailabilities = computed<DayAvailability[]>(() => {
    const schedule = this.businessScheduleRanges();
    const events = this.monthEvents();
    const { year, month } = this.currentCalendarMonth();

    if (!schedule.length) return [];

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const availabilities: DayAvailability[] = [];
    const slotInterval = 15;

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      const dateStr = this.formatDateStr(d);
      const dayOfWeek = d.getDay();
      const daySchedule = schedule.filter(r => r.dayOfWeek === dayOfWeek && r.isActive);

      if (daySchedule.length === 0 || d < today) {
        availabilities.push({
          date: dateStr, totalSlots: 0, availableSlots: 0,
          level: d < today ? 'unknown' : 'none',
        });
        continue;
      }

      // Total slots for this day
      let totalSlots = 0;
      for (const range of daySchedule) {
        const [sh, sm] = range.startTime.split(':').map(Number);
        const [eh, em] = range.endTime.split(':').map(Number);
        for (let t = sh * 60 + sm; t < eh * 60 + em; t += slotInterval) totalSlots++;
      }

      // Events blocking this day
      const dayEvts = events
        .filter(e => e.statusCode !== 'cancelled')
        .map(e => {
          const es = parseDbUtc(e.startDate);
          const ee = parseDbUtc(e.endDate);
          if (es.getFullYear() !== d.getFullYear() || es.getMonth() !== d.getMonth() || es.getDate() !== d.getDate()) return null;
          return { start: es.getHours() * 60 + es.getMinutes(), end: ee.getHours() * 60 + ee.getMinutes() };
        })
        .filter((r): r is { start: number; end: number } => r !== null);

      const isToday = d.toDateString() === today.toDateString();
      const now = new Date();
      const currentMin = isToday ? now.getHours() * 60 + now.getMinutes() : 0;

      let availableSlots = 0;
      for (const range of daySchedule) {
        const [sh, sm] = range.startTime.split(':').map(Number);
        const [eh, em] = range.endTime.split(':').map(Number);
        for (let t = Math.max(sh * 60 + sm, currentMin); t < eh * 60 + em; t += slotInterval) {
          if (!dayEvts.some(er => t >= er.start && t < er.end)) availableSlots++;
        }
      }

      let level: AvailabilityLevel;
      if (totalSlots === 0 || availableSlots === 0) level = 'none';
      else {
        const ratio = availableSlots / totalSlots;
        level = ratio > 0.5 ? 'high' : ratio > 0.2 ? 'medium' : 'low';
      }

      availabilities.push({ date: dateStr, totalSlots, availableSlots, level });
    }

    return availabilities;
  });

  @ViewChild('recurrenceEndDateInput', { static: false }) recurrenceEndDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('timeSlotsGrid', { static: false }) timeSlotsGrid?: ElementRef<HTMLDivElement>;
  private recurrenceEndDatePicker?: FlatpickrInstance;

  // ── Promotions ──
  promotions = signal<Promotion[]>([]);
  promotionsLoading = signal(false);
  selectedPromotion = signal<Promotion | null>(null);

  // ── Misc ──
  drawerOpen = false;
  agendaEvent = signal<AgendaEvent>({} as AgendaEvent);

  // ═══════════════════════════════════════
  //  Computed signals
  // ═══════════════════════════════════════

  readonly totalDuration = computed(() =>
    this.selectedServices().reduce((sum, s) => sum + (s.durationMinutes || 0), 0),
  );

  readonly totalPrice = computed(() =>
    this.selectedServices().reduce((sum, s) => sum + (s.price || 0), 0),
  );

  readonly endTimeSlot = computed(() => {
    const start = this.selectedTimeSlot();
    const dur = this.totalDuration();
    if (!start || !dur) return null;
    const mins = this.timeToMinutes(start) + dur;
    return this.minutesToTime(mins);
  });

  readonly startTimeLabel = computed(() => {
    const t = this.selectedTimeSlot();
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return this.formatTimeLabel(h, m);
  });

  readonly endTimeLabel = computed(() => {
    const t = this.endTimeSlot();
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return this.formatTimeLabel(h, m);
  });

  readonly hasBusinessSchedule = computed(() => this.businessScheduleRanges().length > 0);

  readonly timeSlots = computed<TimeSlot[]>(() => {
    const date = this.selectedDateSignal();
    const ranges = this.businessScheduleRanges();
    if (!date || !ranges.length) return [];

    const dayOfWeek = date.getDay();
    const dayRanges = ranges.filter(r => r.dayOfWeek === dayOfWeek && r.isActive);
    if (!dayRanges.length) return [];

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const events = this.dayEvents();

    // Build list of blocked intervals from existing events
    // Only the chosen professional's appointments make a slot busy
    const professionalId = this.professionalId();
    const blockedIntervals = events
      .filter(e => e.statusCode !== 'cancelled')
      .filter(e => !professionalId || (e.professionalId || (e as any).agendaId) === professionalId)
      .map(e => {
        const start = parseDbUtc(e.startDate);
        const end = parseDbUtc(e.endDate);
        return {
          startMin: start.getHours() * 60 + start.getMinutes(),
          endMin: end.getHours() * 60 + end.getMinutes(),
          label: `${e.clientName || 'Cliente'} - ${e.serviceName || 'Servicio'}`,
          eventId: e.eventId,
        };
      });

    const slots: TimeSlot[] = [];
    for (const range of dayRanges) {
      const [sh, sm] = range.startTime.split(':').map(Number);
      const [eh, em] = range.endTime.split(':').map(Number);
      let cur = sh * 60 + sm;
      const end = eh * 60 + em;
      while (cur < end) {
        const h = Math.floor(cur / 60);
        const m = cur % 60;
        const isPast = isToday && cur < currentMinutes;

        // Check if this slot overlaps with any existing event
        // Exclude the event being edited
        const blockingEvent = blockedIntervals.find(bi =>
          bi.eventId !== this.editEventId && cur >= bi.startMin && cur < bi.endMin
        );

        slots.push({
          time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
          label: this.formatTimeLabel(h, m),
          available: !isPast,
          blocked: !!blockingEvent,
          blockedLabel: blockingEvent?.label,
        });
        cur += 15;
      }
    }
    return slots;
  });

  readonly noSlotsForDay = computed(() => {
    const date = this.selectedDateSignal();
    if (!date) return false;
    const ranges = this.businessScheduleRanges();
    if (!ranges.length) return false;
    const dayOfWeek = date.getDay();
    return !ranges.some(r => r.dayOfWeek === dayOfWeek && r.isActive);
  });

  readonly filteredPromotions = computed(() => {
    const all = this.promotions();
    const services = this.selectedServices();
    if (!services.length) return all;
    return all.filter(promo =>
      !promo.services.length ||
      promo.services.some(ps => services.some(s => s.name.toLowerCase() === ps.toLowerCase())),
    );
  });

  readonly selectedServiceHasSessions = computed(() =>
    this.selectedServices().length === 1 &&
    !!this.selectedService()?.hasSessions &&
    (this.selectedService()?.sessionsCount ?? 0) > 1,
  );

  readonly promotionDiscount = computed(() => {
    const promo = this.selectedPromotion();
    const price = this.totalPrice();
    if (!promo || price <= 0) return 0;
    if (promo.discountType.toLowerCase() === 'fixed') return Math.min(price, promo.value);
    return Math.min(price, (price * promo.value) / 100);
  });

  readonly finalPrice = computed(() => Math.max(0, this.totalPrice() - this.promotionDiscount()));

  readonly isOutsideBusinessHours = computed(() => {
    const date = this.selectedDateSignal();
    const start = this.selectedTimeSlot();
    const end = this.endTimeSlot();
    if (!date || !start || !end) return false;
    const ranges = this.businessScheduleRanges();
    if (!ranges.length) return false;
    const dayOfWeek = date.getDay();
    const dayRanges = ranges.filter(r => r.dayOfWeek === dayOfWeek && r.isActive);
    if (!dayRanges.length) return true;
    const startMins = this.timeToMinutes(start);
    const endMins = this.timeToMinutes(end);
    return !dayRanges.some(r => {
      const [rsh, rsm] = r.startTime.split(':').map(Number);
      const [reh, rem] = r.endTime.split(':').map(Number);
      return startMins >= rsh * 60 + rsm && endMins <= reh * 60 + rem;
    });
  });

  // ── Effects ──
  private finalPriceEffect = effect(() => {
    const price = this.finalPrice();
    setTimeout(() => this.finalPriceChange.emit(price), 0);
  });

  private promotionGuard = effect(() => {
    const available = this.filteredPromotions();
    const selected = this.selectedPromotion();
    if (!this.promotions().length) return;
    if (selected && !available.some(p => p.id === selected.id)) this.clearPromotion();
  });

  // ── Form (simplified – no date/time/service fields) ──
  form = this.fb.group({
    professionalId: [''],
    clientId: [''],
    clientSearch: [''],
    serviceSearch: [''],
    notes: [''],
    promotionId: [''],
    enableRecurrence: [false],
    recurrenceFrequency: [RecurrenceFrequency.Daily],
    recurrenceDaysOfWeek: [[] as number[]],
    recurrenceEndDate: [null as Date | null],
    recurrenceOccurrenceCount: [null as number | null],
  });

  ctrl(name: keyof typeof this.form.controls) {
    return this.form.get(name as string)!;
  }

  serviceResults$!: Observable<Service[]>;

  /** Search runs from 2 characters; null means "nothing searched yet" */
  readonly minClientQuery = 2;
  clientSearching = signal(false);
  activeClientIndex = signal(0);

  clientQuery = toSignal(
    this.form.get('clientSearch')!.valueChanges.pipe(map(v => (v ?? '').trim())),
    { initialValue: '' },
  );

  clientResults = toSignal(
    this.form.get('clientSearch')!.valueChanges.pipe(
      debounceTime(250),
      map(v => (v ?? '').trim()),
      distinctUntilChanged(),
      switchMap(q => {
        this.activeClientIndex.set(0);
        if (q.length < this.minClientQuery) {
          this.clientSearching.set(false);
          return of(null);
        }
        this.clientSearching.set(true);
        return this.businessService
          .searchEntityByBusinessId(this.businessId, 'clients', q)
          .pipe(
            catchError(() => of([] as Client[])),
            finalize(() => this.clientSearching.set(false)),
          );
      }),
    ),
    { initialValue: null as Client[] | null },
  );

  /** The results panel shows while the query is long enough */
  readonly showClientResults = computed(() => this.clientQuery().length >= this.minClientQuery);

  /** Arrow keys move through results, Enter picks, Escape clears the search */
  onClientSearchKeydown(ev: KeyboardEvent) {
    const results = this.clientResults() ?? [];
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        if (results.length) this.activeClientIndex.set((this.activeClientIndex() + 1) % results.length);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        if (results.length) this.activeClientIndex.set((this.activeClientIndex() - 1 + results.length) % results.length);
        break;
      case 'Enter':
        ev.preventDefault();
        if (results[this.activeClientIndex()]) this.selectClient(results[this.activeClientIndex()]);
        break;
      case 'Escape':
        if (this.clientQuery()) {
          ev.stopPropagation();
          this.form.patchValue({ clientSearch: '' });
        }
        break;
    }
  }

  clientDisplayName(c: Client | null): string {
    if (!c) return '';
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return name || c.clientName || c.email || 'Cliente';
  }

  initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
  }

  // ── Step progress, for the numbered sections and the summary ──
  professionalId = toSignal(this.form.get('professionalId')!.valueChanges, {
    initialValue: this.form.get('professionalId')!.value,
  });

  readonly selectedProfessional = computed(
    () => this.professionals().find(p => p.userId === this.professionalId()) ?? null,
  );

  readonly steps = computed(() => ({
    client: !!this.selectedClient(),
    services: this.selectedServices().length > 0,
    professional: !!this.professionalId(),
    time: !!this.selectedDateSignal() && !!this.selectedTimeSlot(),
  }));

  selectProfessional(id: string) {
    this.form.patchValue({ professionalId: id });
  }

  /** Extras shown in the collapsed "Más opciones" summary */
  readonly extrasSummary = toSignal(
    this.form.valueChanges.pipe(
      map(() => {
        const v = this.form.getRawValue();
        const parts: string[] = [];
        if (v.promotionId) parts.push('promoción');
        if ((v.notes ?? '').trim()) parts.push('comentario');
        if (v.enableRecurrence) parts.push('se repite');
        return parts.join(', ');
      }),
    ),
    { initialValue: '' },
  );

  // ═══════════════════════════════════════
  //  Lifecycle
  // ═══════════════════════════════════════

  ngOnInit() {
    this.loadPromotions();
    this.loadBusinessSchedule();

    const initial$ = this.prefetch$.pipe(
      switchMap(() =>
        this.businessService
          .searchEntityByBusinessId(this.businessId, 'services', '')
          .pipe(catchError(() => of([] as Service[]))),
      ),
    );

    const search$ = this.typing$.pipe(
      debounceTime(250),
      map(v => (v ?? '').trim()),
      distinctUntilChanged(),
      switchMap(q =>
        this.businessService
          .searchEntityByBusinessId(this.businessId, 'services', q)
          .pipe(catchError(() => of([] as Service[]))),
      ),
    );

    this.serviceResults$ = merge(initial$, search$).pipe(shareReplay(1));
  }

  ngAfterViewInit() {
    this.form.get('enableRecurrence')?.valueChanges.subscribe(enabled => {
      if (enabled) setTimeout(() => this.initRecurrenceEndDatePicker(), 100);
    });
    if (this.form.get('enableRecurrence')?.value) {
      setTimeout(() => this.initRecurrenceEndDatePicker(), 100);
    }

    setTimeout(() => this.finalPriceChange.emit(this.finalPrice()), 200);

    // Focus client search input when the modal opens (only if no client is selected)
    if (!this.selectedClient() && !this.editEventId) {
      setTimeout(() => {
        (document.getElementById('clientSearch') as HTMLInputElement)?.focus();
      }, 300);
    }
  }

  ngOnDestroy() {
    this.recurrenceEndDatePicker?.destroy();
  }

  ngOnChanges(ch: SimpleChanges) {
    if (ch['selectedDate'] && this.selectedDate) {
      const d = toDate(this.selectedDate);
      this.selectedDateSignal.set(d);
      this.loadDayEvents(d);
      const h = d.getHours();
      const m = d.getMinutes();
      if (h > 0 || m > 0) {
        this.selectedTimeSlot.set(
          `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        );
        this.scrollToSelectedSlot();
      }
    }
    if (this.editEventId) this.loadEvent(this.editEventId!);
  }

  // ═══════════════════════════════════════
  //  Calendar
  // ═══════════════════════════════════════

  onCalendarDateSelected(dateStr: string): void {
    const parts = dateStr.split('-').map(Number);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    this.selectedDateSignal.set(d);
    this.selectedTimeSlot.set(null);
    this.loadDayEvents(d);
  }

  clearSelectedDate(): void {
    this.selectedDateSignal.set(null);
    this.selectedTimeSlot.set(null);
    this.dayEvents.set([]);
  }

  formattedSelectedDate = computed(() => {
    const d = this.selectedDateSignal();
    if (!d) return '';
    return this.formatDateDisplay(d);
  });

  private loadDayEvents(date: Date): void {
    const dateStr = this.formatDateStr(date);
    this.isLoadingDayEvents.set(true);
    this.agendaApi.getEvents({
      businessId: this.businessId,
      startDate: dateStr,
      endDate: dateStr,
    }).pipe(
      finalize(() => this.isLoadingDayEvents.set(false)),
    ).subscribe({
      next: events => this.dayEvents.set(events),
      error: () => this.dayEvents.set([]),
    });
  }

  loadMonthEvents(year: number, month: number): void {
    this.isLoadingMonthAvailability.set(true);
    this.currentCalendarMonth.set({ year, month });

    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    this.agendaApi.getEvents({
      businessId: this.businessId,
      startDate,
      endDate,
    }).pipe(
      finalize(() => this.isLoadingMonthAvailability.set(false)),
    ).subscribe({
      next: events => this.monthEvents.set(events),
      error: () => this.monthEvents.set([]),
    });
  }

  onCalendarMonthChanged(event: { year: number; month: number }): void {
    this.loadMonthEvents(event.year, event.month);
  }

  /** Group time slots by period for display */
  readonly groupedTimeSlots = computed(() => {
    const slots = this.timeSlots();
    const groups: { label: string; icon: string; slots: TimeSlot[] }[] = [];
    const morning: TimeSlot[] = [];
    const afternoon: TimeSlot[] = [];
    const evening: TimeSlot[] = [];

    slots.forEach(slot => {
      const hour = parseInt(slot.time.split(':')[0], 10);
      if (hour < 12) morning.push(slot);
      else if (hour < 17) afternoon.push(slot);
      else evening.push(slot);
    });

    if (morning.length) groups.push({
      label: 'Mañana',
      icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
      slots: morning,
    });
    if (afternoon.length) groups.push({
      label: 'Tarde',
      icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
      slots: afternoon,
    });
    if (evening.length) groups.push({
      label: 'Noche',
      icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
      slots: evening,
    });

    return groups;
  });

  private initRecurrenceEndDatePicker() {
    if (!this.recurrenceEndDateInput?.nativeElement) {
      setTimeout(() => this.initRecurrenceEndDatePicker(), 100);
      return;
    }
    this.recurrenceEndDatePicker?.destroy();
    this.recurrenceEndDatePicker = flatpickr(this.recurrenceEndDateInput.nativeElement, {
      dateFormat: 'Y-m-d',
      locale: Spanish,
      allowInput: false,
      clickOpens: true,
      minDate: this.selectedDateSignal() || 'today',
      defaultDate: this.form.get('recurrenceEndDate')?.value || undefined,
      onChange: dates => {
        if (dates.length > 0) this.form.patchValue({ recurrenceEndDate: dates[0] });
      },
    });
  }

  // ═══════════════════════════════════════
  //  Business schedule
  // ═══════════════════════════════════════

  private loadBusinessSchedule() {
    this.scheduleService.getBusinessSchedule(this.businessId).subscribe({
      next: res => {
        this.businessScheduleRanges.set(res.timeRanges || []);
        // Load month events for calendar availability colors
        const now = new Date();
        this.loadMonthEvents(now.getFullYear(), now.getMonth() + 1);
      },
      error: () => this.businessScheduleRanges.set([]),
    });
  }

  // ═══════════════════════════════════════
  //  Service methods (multi-select)
  // ═══════════════════════════════════════

  onServiceFocus() {
    this.isServiceOpen.set(true);
    const val = this.form.get('serviceSearch')!.value;
    if (!val) setTimeout(() => this.prefetch$.next(), 0);
    else this.typing$.next(val);
  }

  onServiceInput(event: Event) {
    this.typing$.next((event.target as HTMLInputElement).value);
  }

  onServiceBlur() {
    setTimeout(() => this.isServiceOpen.set(false), 150);
  }

  addService(ev: PointerEvent, service: Service) {
    ev.preventDefault();
    if (this.selectedServices().some(s => s.serviceId === service.serviceId)) {
      this.toastService.show('Este servicio ya fue agregado', 'info');
      return;
    }
    this.selectedServices.update(list => [...list, service]);
    this.form.patchValue({ serviceSearch: '' });
    // Keep dropdown open and refresh the list so user can add more services
    this.prefetch$.next();
    this.updateCompatibleProfessionals();
  }

  removeService(serviceId: string) {
    this.selectedServices.update(list => list.filter(s => s.serviceId !== serviceId));
    this.updateCompatibleProfessionals();
    if (!this.selectedServices().length) this.clearPromotion();
  }

  isServiceAlreadySelected(serviceId: string): boolean {
    return this.selectedServices().some(s => s.serviceId === serviceId);
  }

  // ═══════════════════════════════════════
  //  Professional methods
  // ═══════════════════════════════════════

  private updateCompatibleProfessionals() {
    const services = this.selectedServices();
    if (!services.length) {
      this.professionals.set([]);
      this.form.patchValue({ professionalId: '' });
      return;
    }

    this.isLoadingData.set(true);

    if (services.length === 1) {
      this.agendaApi.getProfessionalsByService(this.businessId, services[0].serviceId).subscribe({
        next: list => {
          this.professionals.set(list);
          this.autoSelectProfessional(list);
          this.isLoadingData.set(false);
        },
        error: () => { this.professionals.set([]); this.isLoadingData.set(false); },
      });
    } else {
      forkJoin(
        services.map(s => this.agendaApi.getProfessionalsByService(this.businessId, s.serviceId)),
      ).subscribe({
        next: results => {
          const intersection = results[0].filter(p =>
            results.every(list => list.some(pp => pp.userId === p.userId)),
          );
          this.professionals.set(intersection);
          this.autoSelectProfessional(intersection);
          this.isLoadingData.set(false);
        },
        error: () => { this.professionals.set([]); this.isLoadingData.set(false); },
      });
    }
  }

  private autoSelectProfessional(list: Professional[]) {
    if (list.length === 1) {
      this.form.patchValue({ professionalId: list[0].userId });
    } else {
      const cur = this.form.get('professionalId')?.value;
      if (cur && !list.some(p => p.userId === cur)) this.form.patchValue({ professionalId: '' });
    }
  }

  getProfessionalDisplayName(p: Professional): string {
    const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
    return name || p.email || 'Profesional';
  }

  // ═══════════════════════════════════════
  //  Time slot
  // ═══════════════════════════════════════

  selectTimeSlot(slot: TimeSlot) {
    if (!slot.available) return;
    this.selectedTimeSlot.set(slot.time);
  }

  onManualTimeChange(evt: Event) {
    this.selectedTimeSlot.set((evt.target as HTMLInputElement).value || null);
  }

  private scrollToSelectedSlot() {
    setTimeout(() => {
      const container = this.timeSlotsGrid?.nativeElement;
      if (!container) return;
      const selected = container.querySelector('.ea-slot--selected') as HTMLElement;
      if (selected) {
        // Scroll only within the time slots container, not the entire page
        const containerRect = container.getBoundingClientRect();
        const selectedRect = selected.getBoundingClientRect();
        const scrollTop = selected.offsetTop - container.offsetTop - (containerRect.height / 2) + (selectedRect.height / 2);
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    }, 150);
  }

  formatTimeLabel(hour: number, minute: number): string {
    const period = hour >= 12 ? 'pm' : 'am';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${String(minute).padStart(2, '0')} ${period}`;
  }

  // ═══════════════════════════════════════
  //  Client methods
  // ═══════════════════════════════════════

  selectClient(c: Client) {
    this.selectedClient.set(c);
    this.form.patchValue({ clientId: c.userId, clientSearch: '' });
  }

  clearClient() {
    this.selectedClient.set(null);
    this.form.patchValue({ clientId: '', clientSearch: '' });
  }

  clearClientSearch() {
    this.form.patchValue({ clientSearch: '' });
    setTimeout(() => (document.getElementById('clientSearch') as HTMLInputElement)?.focus(), 0);
  }

  onCreateClientFromSearch(ev: Event) {
    ev.preventDefault();
    ev.stopPropagation();
    this.drawerOpen = true;
    this.form.patchValue({ clientSearch: '' });
  }

  viewClientProfile() {
    const client = this.selectedClient();
    if (client?.userId) {
      window.open(this.router.serializeUrl(this.router.createUrlTree(['/people', client.userId])), '_blank');
    }
  }

  onCreateClient(ev: PointerEvent) {
    ev.preventDefault();
    this.drawerOpen = true;
  }

  onDrawerClose(): void { this.drawerOpen = false; }

  onClientCreated(client: Client): void {
    if (client) {
      this.selectedClient.set(client);
      this.form.patchValue({ clientId: client.userId });
    }
  }

  // ═══════════════════════════════════════
  //  Promotions
  // ═══════════════════════════════════════

  private loadPromotions() {
    this.promotionsLoading.set(true);
    this.promotionsService
      .listPromotions(this.businessId, 'active')
      .pipe(finalize(() => this.promotionsLoading.set(false)))
      .subscribe({
        next: list => { this.promotions.set(list); this.syncPromotionSelectionFromForm(); },
        error: () => this.promotions.set([]),
      });
  }

  onPromotionChange(promotionId: string) {
    if (!promotionId) { this.clearPromotion(); return; }
    const match = this.promotions().find(p => p.id === promotionId) ?? null;
    this.selectedPromotion.set(match);
    this.form.patchValue({ promotionId: match?.id ?? '' });
  }

  promotionLabel(promo: Promotion): string {
    if (promo.discountType?.toLowerCase() === 'percentage') return `${promo.value}%`;
    const fmt = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return fmt.format(typeof promo.value === 'string' ? Number(promo.value) : promo.value);
  }

  clearPromotion() {
    this.selectedPromotion.set(null);
    this.form.patchValue({ promotionId: '' });
  }

  handlePromotionChange(evt: Event) {
    this.onPromotionChange((evt.target as HTMLSelectElement)?.value ?? '');
  }

  private syncPromotionSelectionFromForm() {
    const id = this.form.get('promotionId')?.value;
    this.selectedPromotion.set(id ? (this.promotions().find(p => p.id === id) ?? null) : null);
  }

  // ═══════════════════════════════════════
  //  Recurrence
  // ═══════════════════════════════════════

  toggleRecurrenceDay(day: number): void {
    const days = [...(this.form.get('recurrenceDaysOfWeek')?.value as number[] || [])];
    const i = days.indexOf(day);
    if (i >= 0) days.splice(i, 1); else days.push(day);
    this.form.patchValue({ recurrenceDaysOfWeek: days });
  }

  isRecurrenceDaySelected(day: number): boolean {
    return ((this.form.get('recurrenceDaysOfWeek')?.value as number[]) || []).includes(day);
  }

  onRecurrenceEndTypeChange(type: 'date' | 'count'): void {
    if (type === 'date') this.form.patchValue({ recurrenceOccurrenceCount: null });
    else this.form.patchValue({ recurrenceEndDate: null });
  }

  // ═══════════════════════════════════════
  //  Edit mode
  // ═══════════════════════════════════════

  loadEvent(eventId: string) {
    this.isLoadingData.set(true);
    this.agendaApi.getEventById(eventId).subscribe({
      next: (res: AgendaEvent) => { this.mapEventToForm(res); this.isLoadingData.set(false); },
      error: () => this.isLoadingData.set(false),
    });
  }

  mapEventToForm(event: AgendaEvent) {
    this.agendaEvent.set(event);

    const client: Client = {
      userId: event.clientId, isActive: true, clientName: event.clientName,
      email: event.clientEmail!, phone: event.clientPhone!,
    };
    this.selectedClient.set(client);

    const service: Service = {
      serviceId: event.serviceId, name: event.serviceName,
      description: event.serviceDescription, price: event.servicePrice,
      durationMinutes: event.serviceDurationMinutes,
    };
    this.selectedServices.set([service]);
    this.updateCompatibleProfessionals();

    const start = parseDbUtc(event.startDate);
    this.selectedDateSignal.set(start);
    const h = start.getHours();
    const m = start.getMinutes();
    const rounded = Math.round(m / 30) * 30;
    this.selectedTimeSlot.set(
      `${String(rounded === 60 ? h + 1 : h).padStart(2, '0')}:${String(rounded === 60 ? 0 : rounded).padStart(2, '0')}`,
    );

    const professionalId = (event as any).professionalId || (event as any).agendaId;
    this.form.patchValue({
      clientId: client.userId,
      professionalId,
      promotionId: event.promotionId ?? '',
    });

    this.loadDayEvents(start);
    this.syncPromotionSelectionFromForm();
  }

  // ═══════════════════════════════════════
  //  Modal contract
  // ═══════════════════════════════════════

  isValid(): boolean {
    if (!this.form.get('clientId')?.value) {
      this.toastService.show('Selecciona un cliente', 'warning');
      return false;
    }
    if (!this.selectedServices().length) {
      this.toastService.show('Selecciona al menos un servicio', 'warning');
      return false;
    }
    if (!this.selectedDateSignal()) {
      this.toastService.show('Selecciona una fecha', 'warning');
      return false;
    }
    if (!this.selectedTimeSlot()) {
      this.toastService.show('Selecciona una hora', 'warning');
      return false;
    }
    if (!this.form.get('professionalId')?.value) {
      this.toastService.show('Selecciona un profesional', 'warning');
      return false;
    }
    return true;
  }

  getValue(): any {
    const { clientId, professionalId, notes, promotionId,
      enableRecurrence, recurrenceFrequency, recurrenceDaysOfWeek,
      recurrenceEndDate, recurrenceOccurrenceCount } = this.form.getRawValue();

    const code = 'scheduled' as EventStatusCode;
    const date = this.selectedDateSignal()!;
    const startTime = this.selectedTimeSlot()!;
    const endTime = this.endTimeSlot()!;
    const dateStr = this.formatDateStr(date);

    let recurrence: RecurrenceSettings | null = null;
    if (enableRecurrence) {
      recurrence = {
        frequency: recurrenceFrequency,
        daysOfWeek: recurrenceFrequency === RecurrenceFrequency.Weekly && recurrenceDaysOfWeek?.length
          ? recurrenceDaysOfWeek : null,
        endDate: recurrenceEndDate
          ? (recurrenceEndDate instanceof Date ? recurrenceEndDate.toISOString() : recurrenceEndDate)
          : null,
        occurrenceCount: recurrenceOccurrenceCount || null,
      };
    }

    const services = this.selectedServices();

    // Single service → backward-compatible flat payload
    if (services.length === 1) {
      return {
        clientId,
        startDate: dateStr,
        startTime,
        endDate: dateStr,
        endTime,
        serviceId: services[0].serviceId,
        professionalId,
        notes,
        promotionId: promotionId || null,
        statusId: this.statusDefs[code]?.eventStatusId,
        eventId: this.editEventId,
        recurrence,
      };
    }

    // Multi-service → array of events sharing appointmentGroupId
    const appointmentGroupId = crypto.randomUUID();
    let curStart = this.timeToMinutes(startTime);

    const events = services.map((svc, idx) => {
      const sStart = this.minutesToTime(curStart);
      const sEnd = this.minutesToTime(curStart + (svc.durationMinutes || 30));
      curStart += svc.durationMinutes || 30;
      return {
        clientId,
        startDate: dateStr,
        startTime: sStart,
        endDate: dateStr,
        endTime: sEnd,
        serviceId: svc.serviceId,
        professionalId,
        notes: idx === 0 ? notes : '',
        promotionId: idx === 0 ? (promotionId || null) : null,
        statusId: this.statusDefs[code]?.eventStatusId,
        recurrence,
        appointmentGroupId,
      };
    });

    return { multiService: true, appointmentGroupId, events };
  }

  // ═══════════════════════════════════════
  //  Helpers
  // ═══════════════════════════════════════

  formatDateDisplay(date: Date): string {
    return new Intl.DateTimeFormat('es-CO', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true }).format(value);
  }

  private formatDateStr(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private timeToMinutes(t: string): number {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  }

  private minutesToTime(mins: number): string {
    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  }
}
